import { PLAYFIELD_WIDTH } from '@shared/gameTypes';
import { JUMP_HORIZONTAL_SPEED_MUL, JUMP_PLAYFIELD_HEIGHT } from '@shared/jumpChart';

/**
 * Side-scrolling Jump playfield geometry.
 *
 * The shared engine speaks in playfield units (top-left origin,
 * {@link PLAYFIELD_WIDTH} × {@link JUMP_PLAYFIELD_HEIGHT}). The renderer draws
 * into a canvas whose CSS size depends on the user's window. This module owns
 * the mapping between the two so the renderer never re-derives it.
 *
 * Jump uses the FULL canvas (letterboxed when the aspect ratio doesn't match),
 * with a fixed cube anchor near the left and obstacles scrolling in from the
 * right — the cube only moves vertically, exactly like the engine models it.
 */
export interface JumpLayout {
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
  widthPx: number;
  heightPx: number;
}

export function computeJumpLayout(canvasWidth: number, canvasHeight: number): JumpLayout {
  if (canvasWidth <= 0 || canvasHeight <= 0) {
    return { scaleX: 1, scaleY: 1, offsetX: 0, offsetY: 0, widthPx: 0, heightPx: 0 };
  }
  const sx = canvasWidth / PLAYFIELD_WIDTH;
  const sy = canvasHeight / JUMP_PLAYFIELD_HEIGHT;
  const scale = Math.min(sx, sy);
  const widthPx = PLAYFIELD_WIDTH * scale;
  const heightPx = JUMP_PLAYFIELD_HEIGHT * scale;
  return {
    scaleX: scale,
    scaleY: scale,
    offsetX: (canvasWidth - widthPx) / 2,
    offsetY: (canvasHeight - heightPx) / 2,
    widthPx,
    heightPx,
  };
}

/** Map a playfield X coordinate to a canvas X (no horizontal scroll of the field itself). */
export function playfieldXToCanvas(layout: JumpLayout, playfieldX: number): number {
  return layout.offsetX + playfieldX * layout.scaleX;
}

/** Map a playfield Y coordinate to a canvas Y (cube centre, ground line, spike tips, block tops). */
export function playfieldYToCanvas(layout: JumpLayout, playfieldY: number): number {
  return layout.offsetY + playfieldY * layout.scaleY;
}

/** The cube's fixed horizontal anchor, as a fraction of the visible playfield width. */
export const CUBE_ANCHOR_X_FRAC = 0.22;

/**
 * Runway in raw beats before the {@link JUMP_HORIZONTAL_SPEED_MUL} divisor —
 * mirrors the Waves sibling's constant of the same name.
 */
const BASE_OBSTACLE_BEATS_VISIBLE_AHEAD = 11;

/**
 * Beats of obstacle runway shown ahead of the cube. CONSTANT across every
 * variant and tempo: a fixed runway means a fixed number of playfield units per
 * beat, so the jump arc always has the SAME height AND the SAME horizontal reach
 * (≈1.9:1 distance:height — the Geometry-Dash leap). One invariant arc is what
 * lets players build muscle memory on the jump.
 *
 * The wall-clock scroll speed still tracks the song — it is
 * `unitsPerBeat × bpm/60`, so a faster song scrolls the world past faster while
 * the arc shape and the ~0.9-beat air-time ({@link jumpAirtimeBeats}) stay fixed
 * ("constant jump, variable player speed"). {@link JUMP_HORIZONTAL_SPEED_MUL}
 * sets the ratio (scroll velocity ≈ half the jump's launch velocity).
 *
 * Reaction time in seconds therefore shrinks as BPM rises (a beat is less
 * wall-time). At the fastest shipped song (~203 BPM) the runway is still ≈0.68 s,
 * above the ~0.5 s a first-time player needs; a chart beyond ~275 BPM would dip
 * under that — an accepted consequence of "faster song scrolls faster".
 */
export const JUMP_VISIBLE_BEATS_AHEAD = BASE_OBSTACLE_BEATS_VISIBLE_AHEAD / JUMP_HORIZONTAL_SPEED_MUL;

/**
 * Canvas X where an obstacle at `obstacleBeat` draws when the audio clock is at
 * `currentBeat`. The cube is anchored at {@link CUBE_ANCHOR_X_FRAC}; an
 * obstacle at `currentBeat` lands on the anchor, one `visibleBeats` ahead sits
 * at the right edge. Constant px/beat so the scroll reads as steady forward
 * motion (an obstacle approaches and leaves the cube at the same speed).
 */
export function obstacleCanvasX(
  layout: JumpLayout,
  currentBeat: number,
  obstacleBeat: number,
  visibleBeats: number,
): number {
  const anchorX = layout.offsetX + layout.widthPx * CUBE_ANCHOR_X_FRAC;
  const visibleRight = layout.offsetX + layout.widthPx;
  const pxPerBeat = (visibleRight - anchorX) / visibleBeats;
  return anchorX + (obstacleBeat - currentBeat) * pxPerBeat;
}

/** Inverse of {@link obstacleCanvasX} — the beat whose scroll position lands at `canvasX`. */
export function canvasXToBeat(
  layout: JumpLayout,
  currentBeat: number,
  canvasX: number,
  visibleBeats: number,
): number {
  const anchorX = layout.offsetX + layout.widthPx * CUBE_ANCHOR_X_FRAC;
  const visibleRight = layout.offsetX + layout.widthPx;
  const pxPerBeat = (visibleRight - anchorX) / visibleBeats;
  return currentBeat + (canvasX - anchorX) / pxPerBeat;
}

/** Canvas X of the cube's centre. Constant during a loop — obstacles scroll, the cube doesn't. */
export function cubeCanvasX(layout: JumpLayout): number {
  return layout.offsetX + layout.widthPx * CUBE_ANCHOR_X_FRAC;
}
